import type { GameState } from '../state';
import { sub, gte, type Decimal } from '../math/num';
import { getCoreUpgrade, coreUpgradeCost } from '../data/coreUpgrades';

/** Aggregate permanent bonuses from purchased Core upgrades. */
export interface CoreBonuses {
  damageMult: number;
  scrapMult: number;
  dropMult: number;
  componentMult: number;
  weightBonus: number;
}

export function coreUpgradeLevel(state: GameState, id: string): number {
  return state.meta.coreUpgrades[id] ?? 0;
}

export function coreBonuses(state: GameState): CoreBonuses {
  const lv = (id: string): number => coreUpgradeLevel(state, id);
  return {
    damageMult: 1 + 0.25 * lv('overclock'),
    scrapMult: 1 + 0.3 * lv('scrap_magnet'),
    dropMult: 1 + 0.4 * lv('salvage_luck'),
    componentMult: 1 + 0.3 * lv('reclaimer'),
    weightBonus: 5 * lv('memory'),
  };
}

export function nextCoreUpgradeCost(state: GameState, id: string): Decimal | null {
  const def = getCoreUpgrade(id);
  if (!def) return null;
  const level = coreUpgradeLevel(state, id);
  if (level >= def.maxLevel) return null;
  return coreUpgradeCost(def, level);
}

export function canBuyCoreUpgrade(state: GameState, id: string): boolean {
  const cost = nextCoreUpgradeCost(state, id);
  return cost !== null && gte(state.meta.cores, cost);
}

export function buyCoreUpgrade(state: GameState, id: string): boolean {
  const cost = nextCoreUpgradeCost(state, id);
  if (cost === null || !gte(state.meta.cores, cost)) return false;
  state.meta.cores = sub(state.meta.cores, cost);
  state.meta.coreUpgrades[id] = coreUpgradeLevel(state, id) + 1;
  return true;
}
